import React from 'react';
import { ArrowRight } from 'lucide-react';
import ProductArchitectureFlow from '../diagrams/ProductArchitectureFlow';

const ICONS = {
  promptlens: `${process.env.PUBLIC_URL || ''}/images/products/promptlens-icon.png`,
};

/**
 * Flagship Products showcase — top-of-page highlight for PromptLens & OmniLens.
 */
export default function SectionProducts({ content }) {
  const products = content || {};
  const heading = products.heading ?? 'Products';
  const eyebrow = products.eyebrow ?? 'Flagship platforms';
  const subtext = products.subtext ?? '';
  const items = Array.isArray(products.items) ? products.items : [];
  if (items.length === 0) return null;

  return (
    <section
      id="products"
      className="py-14 sm:py-20 border-t relative overflow-hidden"
      style={{ background: 'var(--jb-forest-bg)', borderColor: 'var(--jb-border)' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 20% 20%, var(--jb-glow-cyan) 0%, transparent 60%), radial-gradient(ellipse 40% 50% at 90% 70%, rgba(224,122,61,0.08) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-3">{eyebrow}</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-text-hero mb-4 tracking-tight">
            {heading}
          </h2>
          {subtext && (
            <p className="font-body text-text-sub text-base sm:text-lg leading-relaxed">{subtext}</p>
          )}
        </div>

        <div className="space-y-8">
          {items.map((item, i) => (
            <article
              key={item.slug}
              className="rounded-2xl border overflow-hidden"
              style={{
                borderColor: 'var(--jb-border)',
                background: 'var(--jb-forest-card)',
              }}
            >
              <div className={`grid lg:grid-cols-2 gap-0 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    {ICONS[item.slug] ? (
                      <img
                        src={ICONS[item.slug]}
                        alt=""
                        className="w-12 h-12 rounded-xl object-cover border"
                        style={{ borderColor: 'var(--jb-border)' }}
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center font-display font-black text-cyan text-lg"
                        style={{ background: 'var(--jb-accent-green-dim)' }}
                      >
                        OL
                      </div>
                    )}
                    {item.badge && (
                      <span
                        className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full"
                        style={{ background: 'var(--jb-cyan-dim)', color: 'var(--jb-cyan)' }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>

                  <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-text-hero mb-2">
                    {item.title}
                  </h3>
                  {item.tagline && (
                    <p className="font-body font-semibold text-cyan mb-3">{item.tagline}</p>
                  )}
                  {item.shortDescription && (
                    <p className="font-body text-text-sub leading-relaxed mb-5">
                      {item.shortDescription}
                    </p>
                  )}

                  {Array.isArray(item.clouds) && item.clouds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {item.clouds.map((c) => (
                        <span
                          key={c}
                          className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full border"
                          style={{ borderColor: 'var(--jb-border)', color: 'var(--jb-text-sub)' }}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}

                  {Array.isArray(item.features) && (
                    <ul className="space-y-2 mb-6">
                      {item.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex items-start gap-2 font-body text-sm text-text-body">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <a
                    href={`#product/${item.slug}`}
                    className="jb-cta inline-flex items-center gap-2 self-start px-6 py-3 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
                  >
                    Explore {item.title}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>

                <div className="p-4 sm:p-6 lg:p-8 flex items-center border-t lg:border-t-0 lg:border-l" style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-forest-bg-2)' }}>
                  <div className="w-full">
                    <ProductArchitectureFlow slug={item.slug} compact />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
